import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, Card, Cluster, Grid, Modal, Stack } from "@salesapp/ui";
import { TipKicker } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatUtc, money } from "./format";
import { Flash } from "./Flash";

interface PaymentRow {
  kind: "payment" | "renewal" | "payment_failed" | "refunded";
  event_id: string;
  status: "paid" | "failed" | "refunded";
  org_id: string;
  org_name: string | null;
  created_at: string | null;
  tier: string | null;
  seats: number | null;
  interval: string | null;
  amount_cents: number | null;
  currency: string | null;
  period_end: string | null;
  error_message: string | null;
  reason?: string | null;
  pf_payment_id: string | null;
  payfast_subscription_payment_id?: string | null;
  payfast_subscription_token?: string | null;
  refund_id?: string | null;
  refunded?: boolean;
  refunded_payment_event_id?: string | null;
  submitted_request?: Record<string, unknown> | null;
  received_response?: Record<string, unknown> | null;
}

interface PaymentsResponse {
  payments: PaymentRow[];
  last_30_days: Array<{ currency: string; collected_cents: number; paid: number; failed: number }>;
}

interface RefundQueryResponse {
  can_refund: boolean;
  reason: string | null;
  pf_payment_id: string;
  amount_cents: number;
  currency: string;
  query: Record<string, unknown>;
}

const KIND_LABEL: Record<PaymentRow["kind"], string> = {
  payment: "First payment",
  renewal: "Renewal",
  payment_failed: "Failed",
  refunded: "Refunded",
};

function paymentId(row: PaymentRow): string {
  return row.refund_id || row.pf_payment_id || "";
}

function refundQueryMessage(row: PaymentRow, query: RefundQueryResponse): string {
  const status = String(query.query.status || "UNKNOWN");
  const available = query.query.amount_available_for_refund;
  const method = query.query.funding_type || query.query.method || "";
  const lines = [
    `PayFast transaction: ${query.pf_payment_id}`,
    `Refund amount: ${money(query.amount_cents, query.currency)}`,
    `PayFast status: ${status}`,
  ];
  if (available != null) lines.push(`Available to refund: ${money(Number(available), query.currency)}`);
  if (method) lines.push(`Funding method: ${String(method)}`);
  if (query.reason) lines.push(`Reason: ${query.reason}`);
  lines.push("");
  lines.push(query.can_refund ? `Create refund for ${paymentId(row) || row.event_id}?` : "Refund cannot be processed.");
  return lines.join("\n");
}

export function PaymentsPage() {
  const [data, setData] = useState<PaymentsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyRefund, setBusyRefund] = useState<string | null>(null);
  const [jsonView, setJsonView] = useState<{ title: string; value: unknown } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<PaymentsResponse>("/operator/payments")
      .then((row) => {
        if (!cancelled) setData(row);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Payments could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function refund(row: PaymentRow) {
    setBusyRefund(row.event_id);
    setError(null);
    try {
      const preflight = await api.post<RefundQueryResponse>(`/operator/payments/${row.event_id}/refund-query`, {
        reason: "requested_by_customer",
      });
      if (!window.confirm(refundQueryMessage(row, preflight))) return;
      if (!preflight.can_refund) return;
      const next = await api.post<PaymentsResponse>(`/operator/payments/${row.event_id}/refund`, {
        reason: "requested_by_customer",
      });
      setData(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Refund could not be processed.");
    } finally {
      setBusyRefund(null);
    }
  }

  if (!data) {
    return error ? <p className="admin-error">{error}</p> : <p>Loading...</p>;
  }

  const totals = data.last_30_days;
  const paid = totals.reduce((sum, row) => sum + row.paid, 0);
  const failed = totals.reduce((sum, row) => sum + row.failed, 0);
  const collected = totals.filter((row) => row.collected_cents > 0);

  return (
    <Stack gap={5}>
      <Cluster gap={3} align="center">
        <h2>Payments</h2>
        <p className="admin-muted">Every charge PayFast reports back through the ITN, plus admin refunds.</p>
      </Cluster>
      <Grid columns={3} gap={3}>
        <Card>
          <Stack gap={2}>
            <TipKicker label="Collected (30 days)" tip="Sum of paid first payments and renewals in the last 30 days, per currency." />
            <p className="admin-kpi">
              {collected.length ? collected.map((row) => money(row.collected_cents, row.currency)).join(" / ") : money(0)}
            </p>
            <p className="admin-muted">{paid} paid invoices</p>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker label="Failed (30 days)" tip="Charges PayFast reported as failed. Sales is also emailed for each one." />
            <p className="admin-kpi">{failed}</p>
            <p className="admin-muted">PayFast retries failed renewals before ending a subscription.</p>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker label="Shown" tip="The most recent payment events, newest first (up to 200)." />
            <p className="admin-kpi">{data.payments.length}</p>
            <p className="admin-muted">most recent first</p>
          </Stack>
        </Card>
      </Grid>
      <Card>
        {error ? <Flash tone="error">{error}</Flash> : null}
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Customer</th>
              <th>Type</th>
              <th>PayFast transaction ID</th>
              <th>PayFast subscription ID</th>
              <th>Amount</th>
              <th>Plan</th>
              <th>Paid through / reason</th>
              <th>Audit JSON</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.payments.length === 0 && (
              <tr>
                <td colSpan={10} className="admin-muted">No payments recorded yet.</td>
              </tr>
            )}
            {data.payments.map((row, index) => (
              <tr key={`${row.event_id}-${row.created_at}-${index}`}>
                <td>{formatUtc(row.created_at)}</td>
                <td>
                  <Link to={`/customers/${row.org_id}`}>{row.org_name || row.org_id}</Link>
                </td>
                <td>
                  <span className={`admin-badge is-${row.status === "failed" ? "deactivated" : "active"}`}>
                    {KIND_LABEL[row.kind] ?? row.kind}
                  </span>
                </td>
                <td>
                  <Stack gap={1}>
                    <code>{paymentId(row) || "-"}</code>
                    {row.refunded_payment_event_id ? <span className="admin-muted">Refund of {row.refunded_payment_event_id}</span> : null}
                  </Stack>
                </td>
                <td>
                  <Stack gap={1}>
                    <code>{row.payfast_subscription_payment_id || "-"}</code>
                    {row.payfast_subscription_token ? (
                      <span className="admin-muted">Token {row.payfast_subscription_token}</span>
                    ) : null}
                  </Stack>
                </td>
                <td>{row.amount_cents != null ? money(row.amount_cents, row.currency || "USD") : "-"}</td>
                <td>
                  {[row.tier, row.seats ? `${row.seats} users` : null, row.interval ? `${row.interval}ly` : null]
                    .filter(Boolean)
                    .join(" / ") || "-"}
                </td>
                <td className="admin-muted">
                  {row.status === "failed" ? row.error_message || "-" : row.period_end ? formatUtc(row.period_end) : row.reason || "-"}
                </td>
                <td>
                  <Cluster gap={1}>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={!row.submitted_request}
                      onClick={() => setJsonView({ title: "Submitted request", value: row.submitted_request })}
                    >
                      View submitted request
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={!row.received_response}
                      onClick={() => setJsonView({ title: "Received response", value: row.received_response })}
                    >
                      View received response
                    </Button>
                  </Cluster>
                </td>
                <td>
                  {row.status === "paid" && row.pf_payment_id && !row.refunded ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={busyRefund === row.event_id}
                      onClick={() => refund(row)}
                    >
                      {busyRefund === row.event_id ? "Refunding..." : "Refund"}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {jsonView ? (
        <Modal isOpen={Boolean(jsonView)} title={jsonView.title} onClose={() => setJsonView(null)} width={760}>
          <pre className="admin-json-view">{JSON.stringify(jsonView.value ?? {}, null, 2)}</pre>
        </Modal>
      ) : null}
    </Stack>
  );
}
